import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  FULFILLMENT_METHODS,
  PAYMENT_METHODS,
  PAYMENT_STATUS_LABELS,
  centsToPesos,
  computeTransactionTotals,
  derivePaymentStatus,
  pesosToCents,
} from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import { Button, ErrorBanner, Field, Spinner, inputCls } from '../../components/ui';
import { Icon, type IconName } from '../../components/Icon';

interface ServiceDto {
  id: string;
  branchId: string;
  branchCode?: string;
  name: string;
  kind: string;
  unit: string;
  priceCents: number;
  minKilos: number | null;
  isActive: boolean;
  createdAt?: string;
}

interface ItemRow {
  description: string;
  quantity: string;
}

const FORM_INPUT = `${inputCls} h-11 text-sm`;

const PAYMENT_ICONS: Record<string, IconName> = {
  CASH: 'cash',
  GCASH: 'wallet',
  MAYA: 'card',
  OTHER: 'card',
};

const PAYMENT_LABELS: Record<string, string> = {
  CASH: 'CASH',
  GCASH: 'GCASH',
  MAYA: 'MAYA',
  OTHER: 'OTHER',
};

function unitSuffix(unit: string): string {
  return unit === 'KG' ? '/kg' : unit === 'PIECE' ? '/pc' : '';
}

function unitHint(service: ServiceDto): string {
  if (service.minKilos != null) return `Minimum ${service.minKilos} kg`;
  if (service.unit === 'KG') return 'Priced per kilogram';
  if (service.unit === 'PIECE') return 'Priced per item';
  return 'Flat rate';
}

function serviceIcon(name: string): IconName {
  return /mix/i.test(name) ? 'inventory' : 'folding';
}

function addonIcon(name: string): IconName {
  if (/fabcon|fabric|detergent|soap|conditioner/i.test(name)) return 'bottle';
  if (/hand|wash/i.test(name)) return 'washing';
  return 'sparkle';
}

/** Numbered section card used by the receiving sheet. */
function SheetSection({
  step,
  title,
  hint,
  icon,
  children,
  className = '',
}: {
  step: number;
  title: string;
  hint?: string;
  icon: IconName;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      aria-labelledby={`receiving-step-${step}`}
      className={`rounded-2xl border border-indigo-100 bg-indigo-50/50 p-3.5 ${className}`}
    >
      <div className="mb-3 flex items-start gap-2.5">
        <span className="mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-deep text-[11px] font-bold text-accent-ink shadow-sm">
          {step}
        </span>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-100/80 text-accent-deep">
          <Icon name={icon} className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
            <h3
              id={`receiving-step-${step}`}
              className="text-sm font-bold tracking-tight text-slate-900"
            >
              {title}
            </h3>
            {hint && <p className="text-[11px] font-medium text-slate-500">{hint}</p>}
          </div>
        </div>
      </div>
      {children}
    </section>
  );
}

/** Map API validation details onto the receiving form. */
function mapFieldErrors(error: ApiError): { fields: Record<string, string>; form?: string } {
  const fields: Record<string, string> = {};
  if (Array.isArray(error.details)) {
    for (const detail of error.details) {
      if (detail && typeof detail === 'object' && 'path' in detail) {
        const path = String((detail as { path: unknown }).path);
        if (!fields[path]) fields[path] = String((detail as { message: unknown }).message);
      }
    }
  }
  if (Object.keys(fields).length > 0) return { fields };
  return { fields: {}, form: error.message };
}

export function ReceivingSheet({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { branches, branchFilter, user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  // Branch: admin picks (defaults to the active filter); employees are locked.
  const defaultBranchId =
    branches.find((b) => b.code === branchFilter)?.id ?? branches[0]?.id ?? '';
  const [branchId, setBranchId] = useState(defaultBranchId);

  // The branch list can arrive after this sheet mounts. Fill the selection
  // once it does, without overriding a branch the admin already chose.
  useEffect(() => {
    if (!branchId && defaultBranchId) setBranchId(defaultBranchId);
  }, [branchId, defaultBranchId]);

  const [services, setServices] = useState<ServiceDto[]>([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [serviceId, setServiceId] = useState('');

  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [itemCount, setItemCount] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [itemRows, setItemRows] = useState<ItemRow[]>([{ description: '', quantity: '' }]);
  const [selectedAddons, setSelectedAddons] = useState<Record<string, number>>({});

  const [paymentMethod, setPaymentMethod] = useState<string>('CASH');
  const [amountPaid, setAmountPaid] = useState('');
  const [fulfillment, setFulfillment] = useState<string>('PICKUP');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [expectedAt, setExpectedAt] = useState('');
  const [notes, setNotes] = useState('');
  const [showTotalBreakdown, setShowTotalBreakdown] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [createdNumber, setCreatedNumber] = useState<string | null>(null);

  // Load the branch-aware catalog whenever the branch changes.
  useEffect(() => {
    if (!branchId) return;
    let cancelled = false;
    setServicesLoading(true);
    setServiceId('');
    setSelectedAddons({});
    void api
      .get<{ services: ServiceDto[] }>(`/services?branch=${branchId}`)
      .then((res) => {
        if (cancelled) return;
        setServices(res.services);
      })
      .catch(() => {
        if (!cancelled) setServices([]);
      })
      .finally(() => {
        if (!cancelled) setServicesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [branchId]);

  // The API returns main services alphabetically. The approved price list was
  // entered in service order (Light, Heavy, Mix, Blankets, comforters), so use
  // the catalog creation order for presentation and fall back to the name.
  const mainServices = useMemo(
    () =>
      services
        .filter((s) => s.kind === 'MAIN')
        .sort((a, b) => {
          const aTime = a.createdAt ? Date.parse(a.createdAt) : Number.NaN;
          const bTime = b.createdAt ? Date.parse(b.createdAt) : Number.NaN;
          if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) {
            return aTime - bTime;
          }
          return a.name.localeCompare(b.name);
        }),
    [services],
  );
  const addonServices = useMemo(() => services.filter((s) => s.kind === 'ADDON'), [services]);
  const selectedService = mainServices.find((s) => s.id === serviceId);

  // Default to the first service in the displayed (catalog) order, and reselect
  // it when the branch changes. Never override a service the user picked.
  useEffect(() => {
    if (servicesLoading || mainServices.length === 0) return;
    if (mainServices.some((s) => s.id === serviceId)) return;
    setServiceId(mainServices[0]!.id);
  }, [mainServices, serviceId, servicesLoading]);
  const requiresWeight =
    selectedService?.unit === 'KG' || selectedService?.minKilos != null;

  const selectedBranch = branches.find((b) => b.id === branchId);
  const branchLabel = selectedBranch
    ? `${selectedBranch.code} — ${selectedBranch.name}`
    : user?.branchCode
      ? `${user.branchCode} — ${user.branchName ?? user.branchCode}`
      : 'Branch not selected';

  // Live total using the SAME function the server uses (server re-computes).
  const preview = useMemo(() => {
    if (!selectedService) return null;
    const count = Number(itemCount) || 0;
    const needsWeight = selectedService.unit === 'KG' || selectedService.minKilos != null;
    const enteredGrams = needsWeight ? Math.round((Number(weightKg) || 0) * 1000) : undefined;
    // A minimum-load flat service has a fixed base price, so its preview is
    // known before weight entry. Submission still sends the real weight and
    // the API enforces the minimum.
    const grams =
      selectedService.minKilos != null
        ? Math.max(enteredGrams ?? 0, selectedService.minKilos * 1000)
        : enteredGrams;
    try {
      return computeTransactionTotals({
        unit: selectedService.unit as 'KG' | 'PIECE' | 'FLAT',
        unitPriceCents: selectedService.priceCents,
        weightGrams: grams,
        itemCount: count,
        minKilos: selectedService.minKilos,
        addons: addonServices
          .filter((a) => (selectedAddons[a.id] ?? 0) > 0)
          .map((a) => ({ unitPriceCents: a.priceCents, quantity: selectedAddons[a.id] })),
      });
    } catch {
      return null; // e.g. KG service without a weight yet
    }
  }, [selectedService, itemCount, weightKg, selectedAddons, addonServices]);

  const breakdownSum = itemRows.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
  const paidCents = amountPaid ? pesosToCents(Number(amountPaid)) : 0;
  const paymentStatus =
    preview && preview.totalCents > 0 ? derivePaymentStatus(paidCents, preview.totalCents) : null;
  const balanceCents = preview ? Math.max(0, preview.totalCents - paidCents) : 0;

  async function doSubmit() {
    if (busy || !selectedService) return;
    setBusy(true);
    setErrors({});
    setFormError(null);

    const payload: Record<string, unknown> = {
      branchId,
      customerName: customerName.trim(),
      customerPhone: customerPhone.trim(),
      serviceId: selectedService.id,
      itemCount: Number(itemCount),
      paymentMethod,
      amountPaidCents: amountPaid ? pesosToCents(Number(amountPaid)) : 0,
      fulfillmentMethod: fulfillment,
      expectedAt: expectedAt ? new Date(expectedAt).toISOString() : undefined,
      notes: notes.trim() || undefined,
    };
    if (selectedService.unit === 'KG' || selectedService.minKilos != null) {
      payload.weightGrams = Math.round((Number(weightKg) || 0) * 1000);
    }
    if (fulfillment === 'DELIVERY') payload.deliveryAddress = deliveryAddress.trim();

    const validRows = itemRows.filter((r) => r.description.trim() && Number(r.quantity) > 0);
    if (validRows.length > 0) {
      payload.items = validRows.map((r) => ({
        description: r.description.trim(),
        quantity: Number(r.quantity),
      }));
    }

    const addons = Object.entries(selectedAddons)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => ({ serviceId: id, quantity: qty }));
    if (addons.length > 0) payload.addons = addons;

    try {
      const res = await api.post<{ transaction: { number: string } }>('/transactions', payload);
      setCreatedNumber(res.transaction.number);
      onCreated();
    } catch (err) {
      if (err instanceof ApiError) {
        const mapped = mapFieldErrors(err);
        setErrors(mapped.fields);
        setFormError(mapped.form ?? null);
      } else {
        setFormError('Something went wrong. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  function resetForNext() {
    setCreatedNumber(null);
    setCustomerName('');
    setCustomerPhone('');
    setItemCount('');
    setWeightKg('');
    setItemRows([{ description: '', quantity: '' }]);
    setShowBreakdown(false);
    setSelectedAddons({});
    setAmountPaid('');
    setDeliveryAddress('');
    setExpectedAt('');
    setNotes('');
    setErrors({});
    setFormError(null);
  }

  /* ---------- success screen ---------- */
  if (createdNumber) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-label="Transaction received"
      >
        <div className="animate-pop max-h-[92dvh] w-full max-w-md overflow-y-auto rounded-2xl border border-indigo-100 bg-panel shadow-2xl">
          <div className="flex flex-col items-center px-6 py-10 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <Icon name="check" className="h-8 w-8" />
            </span>
            <h2 className="mt-4 text-xl font-bold text-slate-900">Transaction received</h2>
            <p className="mt-3 rounded-xl bg-indigo-50 px-5 py-3 font-mono text-2xl font-bold tracking-tight text-indigo-700">
              {createdNumber}
            </p>
            <p className="mt-3 text-sm text-slate-500">
              The laundry is now in the <span className="font-semibold">Pending</span> queue.
            </p>
            <div className="mt-6 flex w-full justify-center gap-2">
              <Button size="lg" onClick={resetForNext} className="w-auto">
                Receive another
              </Button>
              <Button size="lg" variant="secondary" onClick={onClose} className="w-auto">
                Done
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ---------- form ---------- */
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-3 backdrop-blur-sm sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="New transaction"
    >
      <div className="animate-pop flex max-h-[96dvh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-indigo-100 bg-slate-50 shadow-2xl">
        {/* Header */}
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3.5 sm:px-5 sm:py-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent-deep sm:flex">
              <Icon name="purchasing" className="h-6 w-6" />
            </span>
            <div className="min-w-0">
              <h2 className="text-xl font-bold tracking-tight text-slate-900">New Transaction</h2>
              <p className="mt-0.5 text-xs text-slate-500">
                Receiving — number is assigned automatically
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-start gap-2">
            {isAdmin && branches.length > 0 ? (
              <div className="relative">
                <Icon
                  name="branch"
                  className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-accent-deep"
                />
                <span className="pointer-events-none absolute top-1.5 left-9 text-[9px] font-semibold tracking-wide text-slate-500 uppercase">
                  Branch
                </span>
                <select
                  id="rcp-branch"
                  aria-label="Branch"
                  className="h-12 max-w-60 appearance-none rounded-xl border border-indigo-200 bg-indigo-50 pr-8 pl-9 pt-3.5 pb-1 text-xs font-semibold text-slate-900 shadow-sm focus:border-accent focus:ring-2 focus:ring-accent/25 focus:outline-none"
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                >
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.code} — {b.name}
                    </option>
                  ))}
                </select>
                <Icon
                  name="chevronDown"
                  className="pointer-events-none absolute top-1/2 right-2.5 h-4 w-4 -translate-y-1/2 text-slate-500"
                />
              </div>
            ) : (
              <div className="flex h-12 min-w-45 items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-3">
                <Icon name="branch" className="h-4 w-4 shrink-0 text-accent-deep" />
                <div className="min-w-0">
                  <p className="text-[9px] font-semibold tracking-wide text-slate-500 uppercase">
                    Branch
                  </p>
                  <p className="truncate text-xs font-semibold text-slate-900">{branchLabel}</p>
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
            >
              <Icon name="x" className="h-5 w-5" />
            </button>
          </div>
        </header>

        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4 sm:px-5">
          {formError && <ErrorBanner message={formError} />}

          {/* 1 — Customer */}
          <SheetSection step={1} title="Customer Information" icon="users">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Customer name" htmlFor="rcp-name" error={errors.customerName} required>
                <input
                  id="rcp-name"
                  className={FORM_INPUT}
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="e.g. Juan Dela Cruz"
                  required
                />
              </Field>
              <Field
                label="Contact number"
                htmlFor="rcp-phone"
                error={errors.customerPhone}
                hint="Optional"
              >
                <input
                  id="rcp-phone"
                  className={FORM_INPUT}
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  inputMode="tel"
                  placeholder="09xx xxx xxxx"
                />
              </Field>
            </div>
          </SheetSection>

          {/* 2 — Service */}
          <SheetSection
            step={2}
            title="Select Service"
            hint="Choose one main service"
            icon="tag"
          >
            {servicesLoading ? (
              <div className="flex items-center gap-2 rounded-xl bg-white/60 px-4 py-6 text-sm text-slate-500">
                <Spinner className="h-4 w-4 text-accent-deep" /> Loading services…
              </div>
            ) : mainServices.length === 0 ? (
              <ErrorBanner message="No services configured for this branch. Ask an admin to add one in Settings." />
            ) : (
              <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                {mainServices.map((svc) => {
                  const active = svc.id === serviceId;
                  return (
                    <button
                      key={svc.id}
                      type="button"
                      onClick={() => setServiceId(svc.id)}
                      aria-pressed={active}
                      className={`group relative flex min-h-22 items-center gap-2.5 rounded-xl border p-2.5 pr-8 text-left transition-colors ${
                        active
                          ? 'border-accent bg-white shadow-sm ring-2 ring-accent/20'
                          : 'border-indigo-100 bg-white/70 hover:border-indigo-200 hover:bg-white'
                      }`}
                    >
                      <span
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${
                          active
                            ? 'bg-accent text-accent-ink'
                            : 'bg-accent-soft text-accent-deep'
                        }`}
                      >
                        <Icon name={serviceIcon(svc.name)} className="h-6 w-6" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] leading-snug font-semibold text-slate-900">
                          {svc.name}
                        </span>
                        <span className="mt-0.5 block text-[10px] font-medium text-slate-500">
                          {unitHint(svc)}
                        </span>
                        <span
                          className={`mt-1 block text-base font-extrabold tracking-tight ${
                            active ? 'text-accent-deep' : 'text-slate-700'
                          }`}
                        >
                          {centsToPesos(svc.priceCents)}
                          {unitSuffix(svc.unit)}
                        </span>
                      </span>
                      <span
                        aria-hidden="true"
                        className={`absolute top-2.5 right-2.5 flex h-5 w-5 items-center justify-center rounded-full border ${
                          active
                            ? 'border-accent bg-accent text-accent-ink'
                            : 'border-slate-300 bg-white'
                        }`}
                      >
                        {active && <Icon name="check" className="h-3 w-3" />}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {errors.serviceId && <p className="mt-1 text-sm text-rose-600">{errors.serviceId}</p>}
          </SheetSection>

          {/* 3 — Laundry details */}
          <SheetSection step={3} title="Laundry Details" icon="transactions">
            <div className={`grid gap-3 ${requiresWeight ? 'sm:grid-cols-2' : ''}`}>
              <Field
                label="Total items"
                htmlFor="rcp-items"
                error={errors.itemCount}
                hint="Expected count — used for accountability"
                required
              >
                <input
                  id="rcp-items"
                  className={FORM_INPUT}
                  value={itemCount}
                  onChange={(e) => setItemCount(e.target.value)}
                  inputMode="numeric"
                  type="number"
                  min={1}
                  placeholder="e.g. 35"
                  required
                />
              </Field>
              {requiresWeight && (
                <Field
                  label="Weight (kg)"
                  htmlFor="rcp-weight"
                  error={errors.weightGrams}
                  hint={
                    selectedService?.minKilos != null
                      ? `Minimum ${selectedService.minKilos} kg for this load`
                      : undefined
                  }
                  required
                >
                  <input
                    id="rcp-weight"
                    className={FORM_INPUT}
                    value={weightKg}
                    onChange={(e) => setWeightKg(e.target.value)}
                    inputMode="decimal"
                    type="number"
                    step="0.1"
                    min={selectedService?.minKilos ?? 0.1}
                    placeholder={
                      selectedService?.minKilos != null
                        ? `e.g. ${selectedService.minKilos}`
                        : 'e.g. 3.5'
                    }
                    required
                  />
                </Field>
              )}
            </div>

            <div className="mt-3 overflow-hidden rounded-xl border border-indigo-100 bg-white/80">
              <button
                type="button"
                onClick={() => setShowBreakdown((v) => !v)}
                aria-expanded={showBreakdown}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-white"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent-deep">
                  <Icon name="transactions" className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-accent-deep">
                    Item breakdown{' '}
                    <span className="font-normal text-slate-500">(optional)</span>
                  </span>
                  <span className="mt-0.5 block text-[10px] text-slate-500">
                    Add specific items if needed (e.g. shirts, pants, towels).
                  </span>
                </span>
                <span className="hidden items-center gap-1 rounded-lg bg-accent-soft px-3 py-1.5 text-[11px] font-semibold text-accent-deep sm:inline-flex">
                  <Icon name="plus" className="h-3.5 w-3.5" />
                  {showBreakdown ? 'Hide items' : 'Add items'}
                </span>
                <Icon
                  name={showBreakdown ? 'chevronUp' : 'chevronDown'}
                  className="h-4 w-4 shrink-0 text-slate-500"
                />
              </button>

              {showBreakdown && (
                <div className="animate-fade border-t border-indigo-100 px-3 py-3">
                  <div className="space-y-2">
                    {itemRows.map((row, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          className={`${FORM_INPUT} flex-1`}
                          value={row.description}
                          placeholder="e.g. Blankets"
                          aria-label={`Item ${index + 1} description`}
                          onChange={(e) =>
                            setItemRows((rows) =>
                              rows.map((r, i) =>
                                i === index ? { ...r, description: e.target.value } : r,
                              ),
                            )
                          }
                        />
                        <input
                          className={`${FORM_INPUT} w-20`}
                          value={row.quantity}
                          inputMode="numeric"
                          type="number"
                          min={1}
                          placeholder="Qty"
                          aria-label={`Item ${index + 1} quantity`}
                          onChange={(e) =>
                            setItemRows((rows) =>
                              rows.map((r, i) =>
                                i === index ? { ...r, quantity: e.target.value } : r,
                              ),
                            )
                          }
                        />
                        {itemRows.length > 1 && (
                          <button
                            type="button"
                            aria-label={`Remove item ${index + 1}`}
                            onClick={() => setItemRows((rows) => rows.filter((_, i) => i !== index))}
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                          >
                            <Icon name="x" className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        setItemRows((rows) => [...rows, { description: '', quantity: '' }])
                      }
                      className="flex items-center gap-1 text-xs font-semibold text-accent-deep hover:text-accent"
                    >
                      <Icon name="plus" className="h-4 w-4" /> Add row
                    </button>
                    <span
                      className={`text-xs font-medium ${
                        Number(itemCount) > 0 && breakdownSum === Number(itemCount)
                          ? 'text-emerald-600'
                          : 'text-slate-500'
                      }`}
                    >
                      Sum: {breakdownSum}
                      {Number(itemCount) > 0 && ` / ${itemCount}`}
                    </span>
                  </div>
                  {errors.items && <p className="mt-1 text-sm text-rose-600">{errors.items}</p>}
                </div>
              )}
            </div>
          </SheetSection>

          {/* 4 — Add-ons */}
          <SheetSection
            step={4}
            title="Additional Services"
            hint="Select any additional services that apply"
            icon="plus"
          >
            {addonServices.length === 0 ? (
              <p className="rounded-xl border border-dashed border-indigo-200 bg-white/60 px-3 py-3 text-xs text-slate-500">
                No additional services are configured for this branch.
              </p>
            ) : (
              <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                {addonServices.map((addon) => {
                  const qty = selectedAddons[addon.id] ?? 0;
                  const active = qty > 0;
                  return (
                    <div
                      key={addon.id}
                      className={`rounded-xl border bg-white/70 transition-colors ${
                        active ? 'border-accent bg-white shadow-sm' : 'border-indigo-100'
                      }`}
                    >
                      <button
                        type="button"
                        aria-pressed={active}
                        onClick={() =>
                          setSelectedAddons((prev) => ({
                            ...prev,
                            [addon.id]: active ? 0 : 1,
                          }))
                        }
                        className="flex w-full items-start gap-2.5 p-2.5 text-left"
                      >
                        <span
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                            active
                              ? 'bg-accent text-accent-ink'
                              : 'bg-accent-soft text-accent-deep'
                          }`}
                        >
                          <Icon name={addonIcon(addon.name)} className="h-5 w-5" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[11px] leading-snug font-semibold text-slate-900">
                            {addon.name}
                          </span>
                          <span className="mt-0.5 block text-xs font-extrabold text-slate-700">
                            {centsToPesos(addon.priceCents)}
                          </span>
                        </span>
                        <span
                          aria-hidden="true"
                          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                            active
                              ? 'border-accent bg-accent text-accent-ink'
                              : 'border-slate-300 bg-white'
                          }`}
                        >
                          {active && <Icon name="check" className="h-3 w-3" />}
                        </span>
                      </button>
                      {active && (
                        <div className="flex items-center gap-1 border-t border-indigo-100 px-2.5 py-1.5">
                          <span className="mr-auto text-[10px] font-medium text-slate-500">
                            Quantity
                          </span>
                          <button
                            type="button"
                            aria-label={`Decrease ${addon.name}`}
                            onClick={() =>
                              setSelectedAddons((prev) => ({
                                ...prev,
                                [addon.id]: Math.max(0, (prev[addon.id] ?? 0) - 1),
                              }))
                            }
                            className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-100 text-sm font-bold text-slate-700 hover:bg-slate-200"
                          >
                            −
                          </button>
                          <span className="min-w-5 text-center text-xs font-bold text-slate-900">
                            {qty}
                          </span>
                          <button
                            type="button"
                            aria-label={`Increase ${addon.name}`}
                            onClick={() =>
                              setSelectedAddons((prev) => ({
                                ...prev,
                                [addon.id]: (prev[addon.id] ?? 0) + 1,
                              }))
                            }
                            className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-100 text-sm font-bold text-slate-700 hover:bg-slate-200"
                          >
                            +
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </SheetSection>

          <div className="grid gap-3 lg:grid-cols-2">
            {/* 5 — Payment */}
            <SheetSection step={5} title="Payment Details" icon="card" className="flex flex-col">
              <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_168px]">
                <div>
                  <p className="mb-1.5 text-sm font-medium text-slate-600">Payment method</p>
                  <div className="grid grid-cols-2 gap-2">
                    {PAYMENT_METHODS.map((method) => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => setPaymentMethod(method)}
                        aria-pressed={paymentMethod === method}
                        className={`flex h-10 items-center justify-center gap-1.5 rounded-lg border px-2 text-[11px] font-bold tracking-wide transition-colors ${
                          paymentMethod === method
                            ? 'border-accent bg-white text-accent-deep shadow-sm ring-2 ring-accent/20'
                            : 'border-indigo-100 bg-white/70 text-slate-600 hover:bg-white'
                        }`}
                      >
                        <Icon name={PAYMENT_ICONS[method] ?? 'card'} className="h-4 w-4" />
                        {PAYMENT_LABELS[method] ?? method}
                      </button>
                    ))}
                  </div>
                </div>
                <Field
                  label="Amount paid"
                  htmlFor="rcp-paid"
                  error={errors.amountPaidCents}
                  hint="Leave blank if paying later"
                >
                  <input
                    id="rcp-paid"
                    className={`${FORM_INPUT} text-right tabular-nums`}
                    value={amountPaid}
                    onChange={(e) => setAmountPaid(e.target.value)}
                    inputMode="decimal"
                    type="number"
                    step="0.01"
                    min={0}
                    placeholder="0.00"
                  />
                </Field>
              </div>
              <div className="mt-auto pt-3">
                <div className="flex items-center justify-between gap-3 rounded-lg border border-indigo-100 bg-white/60 px-3 py-2">
                  <span className="text-[11px] font-medium text-slate-500">Payment status</span>
                  <span
                    className={`text-right text-xs font-semibold ${
                      paymentStatus === 'PAID'
                        ? 'text-emerald-700'
                        : paymentStatus === 'PARTIAL'
                          ? 'text-amber-700'
                          : 'text-slate-600'
                    }`}
                  >
                    {paymentStatus ? PAYMENT_STATUS_LABELS[paymentStatus] : 'No total yet'}
                    {paymentStatus === 'PARTIAL' && balanceCents > 0
                      ? ` · ${centsToPesos(balanceCents)} due`
                      : ''}
                  </span>
                </div>
              </div>
            </SheetSection>

            {/* 6 — Fulfillment */}
            <SheetSection step={6} title="Fulfillment" icon="pickup">
              <div className="grid grid-cols-2 gap-2">
                {FULFILLMENT_METHODS.map((method) => (
                  <button
                    key={method}
                    type="button"
                    onClick={() => setFulfillment(method)}
                    aria-pressed={fulfillment === method}
                    className={`flex h-11 items-center justify-center gap-2 rounded-xl border text-xs font-semibold transition-colors ${
                      fulfillment === method
                        ? 'border-accent bg-white text-accent-deep shadow-sm ring-2 ring-accent/20'
                        : 'border-indigo-100 bg-white/70 text-slate-600 hover:bg-white'
                    }`}
                  >
                    <Icon name={method === 'PICKUP' ? 'pickup' : 'delivery'} className="h-4 w-4" />
                    {method === 'PICKUP' ? 'Pickup' : 'Delivery'}
                  </button>
                ))}
              </div>

              {fulfillment === 'DELIVERY' && (
                <div className="mt-3">
                  <Field
                    label="Delivery address"
                    htmlFor="rcp-address"
                    error={errors.deliveryAddress}
                    required
                  >
                    <input
                      id="rcp-address"
                      className={FORM_INPUT}
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      placeholder="Street, barangay, city"
                      required
                    />
                  </Field>
                </div>
              )}

              <div className="mt-3 grid grid-cols-2 gap-3">
                <Field
                  label="Expected completion"
                  htmlFor="rcp-expected"
                  error={errors.expectedAt}
                  hint="Optional"
                >
                  <input
                    id="rcp-expected"
                    className={`${FORM_INPUT} px-2.5 text-xs`}
                    value={expectedAt}
                    onChange={(e) => setExpectedAt(e.target.value)}
                    type="datetime-local"
                  />
                </Field>
                <Field label="Notes" htmlFor="rcp-notes" error={errors.notes} hint="Optional">
                  <input
                    id="rcp-notes"
                    className={`${FORM_INPUT} px-2.5 text-xs`}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. separate whites"
                    maxLength={500}
                  />
                </Field>
              </div>
            </SheetSection>
          </div>
        </div>

        {/* Footer: live total + submit */}
        <footer className="border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-12px_30px_-24px_rgba(15,23,42,0.35)] backdrop-blur">
          {showTotalBreakdown && preview && (
            <div className="animate-fade mb-2 space-y-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
              <div className="flex justify-between gap-3">
                <span className="text-slate-500">
                  Main service
                  {selectedService ? ` — ${selectedService.name}` : ''}
                </span>
                <span className="font-semibold text-slate-800">
                  {centsToPesos(preview.baseCents)}
                </span>
              </div>
              {preview.addonCents > 0 && (
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">Additional services</span>
                  <span className="font-semibold text-slate-800">
                    {centsToPesos(preview.addonCents)}
                  </span>
                </div>
              )}
              {paidCents > 0 && (
                <>
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">Amount paid</span>
                    <span className="font-semibold text-slate-800">{centsToPesos(paidCents)}</span>
                  </div>
                  <div className="flex justify-between gap-3 border-t border-slate-200 pt-1">
                    <span className="text-slate-500">Balance</span>
                    <span className="font-semibold text-slate-800">
                      {centsToPesos(balanceCents)}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent-deep">
              <Icon name="purchasing" className="h-5 w-5" />
            </span>
            <div className="min-w-30">
              <p className="text-sm font-bold text-slate-900">Transaction Total</p>
              <p className="text-[11px] text-slate-500">
                {preview && preview.addonCents > 0
                  ? 'Main service + additional services'
                  : 'Main service'}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <div className="text-right">
                <p className="text-xl font-extrabold tracking-tight text-slate-900 tabular-nums">
                  {preview ? centsToPesos(preview.totalCents) : '—'}
                </p>
                {paymentStatus && (
                  <p className="text-[11px] text-slate-500">
                    {PAYMENT_STATUS_LABELS[paymentStatus]}
                    {paidCents > 0 && balanceCents > 0
                      ? ` · Balance ${centsToPesos(balanceCents)}`
                      : ''}
                  </p>
                )}
              </div>
              <button
                type="button"
                aria-label={showTotalBreakdown ? 'Hide total breakdown' : 'Show total breakdown'}
                aria-expanded={showTotalBreakdown}
                onClick={() => setShowTotalBreakdown((v) => !v)}
                disabled={!preview}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Icon
                  name={showTotalBreakdown ? 'chevronDown' : 'chevronUp'}
                  className="h-4 w-4"
                />
              </button>
            </div>

            <div className="ml-auto flex gap-2">
              <Button variant="secondary" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button
                onClick={() => void doSubmit()}
                disabled={busy || !selectedService || !customerName.trim() || !Number(itemCount)}
              >
                {busy ? <Spinner className="h-5 w-5" /> : <Icon name="check" className="h-5 w-5" />}
                Receive
              </Button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
